/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import Media from "react-media";

import UnitedHeaderLogic from "./UnitedHeaderLogic";
import Gettext from "./common/Gettext";
import { NEWS_LINK } from "./common/fetch";
import { Link, OptionalLink } from "./common/Link";
import { mapStateToTime } from "./Timers";
import { isIronTerminalIosApp } from "./utils";
import { LangSelect } from "./Navigation/Language";
import Navigation from "./Navigation/Navigation";
import ToggleNav from "./Navigation/Hamburger";
import HeaderMain from "./HeaderMain";
import { isProfileComplete } from "./Private";
import { HelpdeskHeader, HelpdeskSearch } from "./Helpdesk/Header";

import { IronRedesignThemeProvider } from "./ThemeProvider";
import { fontSize } from "./common/styled-mixins";
import { gaReducer } from "./GA";
import withErrorCatch from "./withErrorCatch";
import { useSuggestedPromoCode } from "./Bell/Labels";
import { useBonusTimerApi } from "./hooks/useBonusTimer";

const activeClassName = "active";

const HeaderContainer = styled.div`
  background-color: #1b546a;
  color: #feac00;

`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  max-width: 1300px;
  height: 48px;
  margin: 0 auto;
  padding: 0 5px;

  ${(props) =>
    props.showLinks &&
    css`
      @media (max-width: 959px) {
        height: 96px;
        padding-bottom: 48px;
      }
    `};

  @media (min-width: 960px) {
    height: 75px;
    padding-left: 25px;
  }

  ${({ isLoggedIn, isHelpdesk }) =>
    isLoggedIn &&
    isHelpdesk &&
    css`
      @media (min-width: 960px) {
        padding-left: 10px;
      }

      @media (min-width: 1100px) {
        padding-left: 25px;
      }
    `};

  ${(props) =>
    props.isApp &&
    css`
      @media (max-width: 959px) {
        height: 55px;
        padding: 0;
      }
    `};
`;

const LeftContainer = styled.div`
  position: relative;
  display: flex;
`;

const RightContainer = styled(LeftContainer)`
  @media (min-width: 960px) {
    margin-left: 10px;
  }
`;

const MiddleContainer = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  height: 75px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  align-content: center;

  @media (max-width: 959px) {
    height: 55px;
    width: 100%;
    justify-content: space-between;
  }

  @media (min-width: 960px) {
    position: static;
    flex-grow: 1;
  }
`;

const LinkItem = styled(OptionalLink).attrs({
  activeClassName,
})`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  height: 100%;
  padding: 0 10px;
  font-size: ${fontSize(12)};
  line-height: 1;
  text-align: center;
  text-decoration: none;
  color: ${(props) => props.theme.lightWhiteForeground};
  box-shadow: inset 0 -4px 0 0 transparent;

  ${(props) =>
    props.isMainPage &&
    css`
      color: rgba(255, 255, 255, 0.9);
      box-shadow: none;
      text-decoration: underline;
    `};

  
  ${(props) =>
      props.active &&
      css`
        color: #fff;
        box-shadow: inset 0 4px 0 0 ${(props) => props.theme.accent};
    
        ${(props) =>
          props.isMainPage &&
          css`
        box-shadow: none;
      `};
  `};
 

  &.${activeClassName} {
    color: #fff;
    box-shadow: inset 0 4px 0 0 ${(props) => props.theme.accent};

    ${(props) =>
      props.isMainPage &&
      css`
        box-shadow: none;
      `};
  }

  @media (min-width: 960px) {
    font-size: ${fontSize(14)};
    margin-left: 10px;
  }

  @media (max-width: 959px) {
    flex-grow: 1;
    flex-shrink: 0;
    width: 25%;
    height: 100%;

   
  }
`;

const AboutLink = styled(LinkItem).attrs({
  activeClassName,
})`
  &.${activeClassName} {
    ${(props) =>
      !props.isAbout &&
      css`
        color: ${(props) => props.theme.staticHeaderLink};
        box-shadow: inset 0 -4px 0 0 transparent;
      `};

    ${(props) =>
      props.isMainPage &&
      css`
        color: rgba(255, 255, 255, 0.5);
        box-shadow: none;
      `};
  }
`;

const LinksContainer = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  align-content: center;
  flex-grow: 1;
  height: 100%;

  @media (min-width: 960px) {
    ${(props) =>
      props.isHelpdesk &&
      css`
        margin-left: 56px;

        & ${LinkItem} {
          box-shadow: none;
        }
      `};

    ${({ noMargin }) =>
      noMargin &&
      css`
        margin-left: 0;

        > a {
          margin-left: 5px;
        }
      `};
  }

  @media (min-width: 1100px) {
    ${({ noMargin }) =>
      noMargin &&
      css`
        margin-left: 0;

        > a {
          margin-left: 10px;
        }
      `};
  }

  @media (max-width: 959px) {
    justify-content: space-between;
  }
`;

const ChooseLanguage = styled.div`
  direction: ${({ theme }) => theme.direction};
  position: relative;
  align-self: center;
  width: 88px;
  height: 39px;
  margin-right: 4px;
  margin-left: 20px;
  font-size: ${fontSize(12)};
  line-height: 1;
  color: #fff;
  text-transform: uppercase;
  cursor: pointer;
  user-select: none;
  z-index: 11;

  @media (max-width: 480px) {
    margin-left: 0;
    margin-right: 0;
  }

  ${({ noMargin }) =>
    noMargin &&
    css`
      @media (min-width: 960px) {
        margin-left: 5px;
      }

      @media (min-width: 1100px) {
        margin-left: 20px;
      }
    `};
`;

const TradeIcon = styled.div`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: 17px;
  height: 19px;
  margin-right: 12px;
  margin-top: 3px;

  @media (max-width: 959px) {
    width: 17px;
    height: 22px;
    margin-right: 5px;
  }
`;

const Button = styled(Link)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  height: 47px;
  padding: 0 20px;
  margin-right: 0;
  font-weight: 400;
  font-size: 18px;
  text-shadow: -1px -1px rgba(0, 0, 0, 0.2);
  text-decoration: none;
  color: #fff;
  border-radius: 8px;
  border: none;
  border-bottom: 2px solid;
  cursor: pointer;

  :hover,
  :active,
  :focus {
    color: #fff;
  }

  ${({ theme, type }) => {
    switch (type) {
      case "deposit":
        return css`
          border-bottom-color: ${(props) => props.theme.upperBorder};
          background: #60a31c;
          background: linear-gradient(to top, #60a31c, #68c703);

          :hover,
          :active,
          :focus {
            background: #68c703;
            background: linear-gradient(to top, #68c703, #60a31c);
          }
        `;

      case "trade":
      default:
        return css`
          border-bottom-color: #cd7900;
          background: #fd9500;
          background: linear-gradient(
            to top,
            #fd9500,
            ${(props) => props.theme.accent}
          );

          :hover,
          :active,
          :focus {
            background: ${(props) => props.theme.accent};
            background: linear-gradient(
              to top,
              ${(props) => props.theme.accent},
              #fd9500
            );
          }
        `;
    }
  }};
  ${process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
    css`
      background: none;
      border: 2px solid #ffffff;
      box-shadow: none;
      text-shadow: none;

      :hover,
      :active,
      :focus {
        background: #ffffff;
        color: #1b546a;
        box-shadow: none;
        text-shadow: none;

        ${TradeIcon} {
          color: #1b546a;
        }
      }

      @media (max-width: 959px) {
        font-weight: 700;
      }
    `};

  @media (max-width: 959px) {
    min-width: 0;
    height: 30px;
    padding: 0 10px;
    font-size: ${fontSize(12)};
    border-radius: 4px;
    color: #fff;
  }
`;


const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleNavContainer = styled.div`
  display: flex;
  margin-left: -19px;
`;

const ToggleNavMobile = styled.div`
  display: flex;
  align-self: center;
  margin-top: -4px;
  margin-left: 5px;
`;

type TradeButtonProps = {
  isHelpdesk?: boolean,
  className?: string,
  onClick?: () => void,
};

const ApplicationMiddleLinks = ({ isHelpdesk, pathname, isMainPage }) => (
  <LinksContainer isHelpdesk={isHelpdesk}>
    {/* <Media query="(max-width: 959px)">
      {FeatureFlags.enableBell && <Bell />}
    </Media> */}
    <LinkItem to="/" active={pathname === "/"}>
        <Gettext>Analytics</Gettext>
    </LinkItem>

    <LinkItem
        to="/contacts"
        href={NEWS_LINK}
        active={pathname.indexOf('contacts') > 0}
    >
    <Gettext>Contacts</Gettext>
    </LinkItem>


  </LinksContainer>
);

const ApplicationHeader = ({ isMainPage, showLinks }) => (
  <HeaderContainer isMainPage={isMainPage}>
    <Header showLinks={showLinks()} isApp={true}>
      <MiddleContainer>
        <ApplicationMiddleLinks pathname={window.location.pathname} />
      </MiddleContainer>
    </Header>
  </HeaderContainer>
);

export function TradeButton(props: TradeButtonProps) {
  return null;
}

const DepositButton = connect(mapStateToTime)(
  ({ suggestedPromoCode, time }) => {
    const bonus = useBonusTimerApi({ time });

    if (bonus == null || bonus.duration < 0 || bonus.status !== "available") {
      return (
        <Button
          type="deposit"
          to={
            suggestedPromoCode != null
              ? "/fillup?promocode=" + suggestedPromoCode
              : "/fillup"
          }
          onClick={(e) => {
            gaReducer("CLICKLINK_TRADE_DEPOSIT_HEADER", {
              linkText: e.currentTarget.textContent,
              pageUrl: window.document.location.href,
            });
          }}
          className="intercept-noauth"
        >
          <Gettext>Deposit</Gettext>
        </Button>
      );
    }

    return (
      <Button
        type="deposit"
        to={
          bonus.promo != null ? "/fillup?promocode=" + bonus.promo : "/fillup"
        }
        onClick={(e) => {
          gaReducer("CLICKLINK_TRADE_DEPOSIT_HEADER", {
            linkText: e.currentTarget.textContent,
            pageUrl: window.document.location.href,
          });
        }}
        className="intercept-noauth"
      >
        <Gettext>Deposit</Gettext>
      </Button>
    );
  }
);

type MiddleLinksProps = {
  isHelpdesk?: boolean,
  isNews?: boolean,
  pathname: string,
  isMainPage: boolean,
  noMargin?: boolean,
};

function MiddleLinks({
  isHelpdesk,
  isNews,
  pathname,
  isMainPage,
  noMargin,
}: MiddleLinksProps) {
  const isAbout = pathname.split("/")[1] === "aboutus";
    const isContacts = pathname.split("/")[1] === "contacts";

  return (
    <LinksContainer isHelpdesk={isHelpdesk} noMargin={noMargin}>
      {/* <Media query="(max-width: 959px)">
        {FeatureFlags.enableBell && <Bell />}
      </Media> */}
      <React.Fragment>
        <LinkItem
            to="/"
            active={isMainPage}
        >
        <Gettext>News</Gettext>
        </LinkItem>
        <AboutLink
          to="/aboutus"
          active={isAbout}
          isMainPage={isMainPage}
        >
          <Gettext>About us</Gettext>
        </AboutLink>
        <LinkItem
        to="/contacts"
        href={NEWS_LINK}
        active={isContacts}
            >
        <Gettext>Contacts</Gettext>
        </LinkItem>
      </React.Fragment>
    </LinksContainer>
  );
}

window.isApp =
  /wvbo/g.test(window.navigator.userAgent) &&
  !/wvbo_full/g.test(window.navigator.userAgent);
window.isFullAndroidApp = /wvbo_full/g.test(window.navigator.userAgent);

function UnitedHeader(props: { hideTradeButton?: boolean }) {
  let suggestedPromoCode = useSuggestedPromoCode();

  if (
    /ironterminal/g.test(window.navigator.userAgent.toLowerCase()) ||
    isIronTerminalIosApp
  )
    return null;

  const shouldDisplayMenuButton =
    window.location.pathname !== "/password-update";

  return (
    <UnitedHeaderLogic {...props}>
      {({
        dispatch,
        onLocaleChange,
        match,
        location,
        history,
        query,
        isIOS,
        appVersion,
        currencySettings,
        isAndroidApp,
        isVIP,
        geo,
        locale,
        locales,
        profile,
        profileStatus,
        authTab,
        shouldDisplayMainHeader,
        showMainHeader,

        showDeposit,

        changeLocaleFetching,
        isNavActive,

        isLoggedIn,
        isAppLogin,
        isHelpdesk,
        isNews,
        isMainPage,
        showLangSelect,
        isMobileApp,
        isInnerHeader,

        toggle,
        hide,
        showTopLinks,
        showLinks,
        showLoginForm,
        changeXLocaleWithLocale,
      }) => (
        <IronRedesignThemeProvider
          isInnerHeader={isInnerHeader}
          theme={
            profile && profile.currency === "THB" ? "thProjectTrade" : null
          }
        >
          <React.Fragment>
            {window.isApp ? (
              <ApplicationHeader
                isMainPage={isMainPage}
                showLinks={showLinks}
                isLoggedIn={isLoggedIn}
                isHelpdesk={isHelpdesk}
                isNews={isNews}
                pathname={location.pathname}
              />
            ) : (
              (!isAndroidApp || window.isFullAndroidApp) &&
              !(isIOS && appVersion >= 2000000) && (
                <HeaderContainer
                  isMainPage={isMainPage}
                  isTransparent={props.isTransparent}
                >
                  <Header
                    showLinks={showLinks()}
                    isLoggedIn={isLoggedIn}
                    isHelpdesk={isHelpdesk}
                  >
                    <LeftContainer>
                      {shouldDisplayMenuButton &&
                        isLoggedIn &&
                        isProfileComplete(profile) && (
                          <Media query="(max-width: 959px)">
                            <ToggleNavMobile>
                              <ToggleNav
                                active={isNavActive}
                                onClick={toggle}
                                isIOS={isIOS}
                              />
                            </ToggleNavMobile>
                          </Media>
                        )}

                      {/* <Media query="(min-width: 960px)">
                        {FeatureFlags.enableBell && (
                          <Bell
                            className="intercept-noauth"
                            noMargin={isLoggedIn && (isHelpdesk || isNews)}
                          />
                        )}
                      </Media> */}
                    </LeftContainer>
                    {showLinks() && (
                      <MiddleContainer>
                        {isHelpdesk || isNews ? (
                          <HelpdeskSearch>
                            <MiddleLinks
                              isMainPage={isMainPage}
                              isHelpdesk={isHelpdesk}
                              noMargin={isLoggedIn}
                              isNews={isNews}
                              pathname={location.pathname}
                            />
                          </HelpdeskSearch>
                        ) : (
                          <MiddleLinks
                            pathname={location.pathname}
                            isMainPage={isMainPage}
                          />
                        )}
                      </MiddleContainer>
                    )}
                    <RightContainer>
                      {showTopLinks() && (
                        <ButtonsContainer>
                          {props.hideTradeButton !== true && (
                            <TradeButton className="intercept-noauth" />
                          )}

                          {isLoggedIn &&
                            isProfileComplete(profile) &&
                            match.path !== "/fillup" &&
                            match.path !== "/billing/offline" &&
                            showDeposit === true && (
                              <DepositButton
                                suggestedPromoCode={suggestedPromoCode}
                              />
                            )}
                        </ButtonsContainer>
                      )}

                      {showLangSelect && (
                        <ChooseLanguage
                          noMargin={isLoggedIn && (isHelpdesk || isNews)}
                        >
                          <LangSelect
                            locale={locale}
                            locales={locales}
                            showLoader={changeLocaleFetching}
                            onLocaleChange={changeXLocaleWithLocale}
                          />
                        </ChooseLanguage>
                      )}
                      {shouldDisplayMenuButton &&
                        isLoggedIn &&
                        isProfileComplete(profile) && (
                          <Media query="(min-width: 960px)">
                            <ToggleNavContainer className="intercept-noauth">
                              <ToggleNav
                                active={isNavActive}
                                onClick={toggle}
                                isIOS={isIOS}
                              />
                            </ToggleNavContainer>
                          </Media>
                        )}
                    </RightContainer>

                    <Navigation
                      open={isNavActive}
                      onClose={hide}
                      profile={profile}
                      changeLocaleFetching={changeLocaleFetching}
                      onLocaleChange={changeXLocaleWithLocale}
                      isVIP={isVIP}
                      hideLangSelect={true}
                    />
                  </Header>
                </HeaderContainer>
              )
            )}
            {isHelpdesk &&
              process.env.REACT_APP_DEPLOY_TARGET !== "eztrader" && (
                <HelpdeskHeader showContactUs={profile && profile.real === 1} />
              )}
            {shouldDisplayMainHeader && (
              <HeaderMain
                query={query}
                authTab={authTab}
                geo={geo}
                isVIP={isVIP}
                profile={profile}
                isLoggedIn={isLoggedIn}
                currencySettings={currencySettings}
                profileStatus={profileStatus}
                dispatch={dispatch}
                isAppLogin={isAppLogin}
                location={location}
                history={history}
                isHelpdesk={isHelpdesk}
              />
            )}
          </React.Fragment>
        </IronRedesignThemeProvider>
      )}
    </UnitedHeaderLogic>
  );
}

export default withErrorCatch(UnitedHeader);



// WEBPACK FOOTER //
// ./src/components/UnitedHeader.js